import { useState, useCallback, useMemo } from 'react';
import type { ConnectVisitData } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import type { WidgetConfig } from '@hotjar/feedback/components/common/Wizard/WidgetPreview/WidgetPreview';
import { tokens, Textarea, Alert, Stack, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { DragStart, DropResult } from 'react-beautiful-dnd';
import { DragDropContext } from 'react-beautiful-dnd';
import { useNavigation } from '@hotjar/navigation';
import type { ReactionStyle } from '@hotjar/feedback';
import { useTranslation } from 'react-i18next';
import { rtlLanguages } from '@hotjar/insights-common/constants/legacy';
import { QuestionList } from '../QuestionList/QuestionList';
import type { QuestionTypes, SurveyQuestion } from '../../../../../domain/Surveys/Questions';
import { PreviewAndQuestionBankPanel } from './PreviewAndQuestionBankPanel';
import type { Tab } from './PreviewAndQuestionBankPanel';
import { QuestionContainer } from './QuestionContainer';
import { ALL_QUESTIONS } from '../QuestionBank/QuestionBank';
import type { QuestionBankQuestion } from '../QuestionBank/constants';
import { LegalInformation } from './LegalInformation';
import { QuestionConnectVisitData } from '../QuestionConnectVisitData';
import { customSensors } from '../../../../common/DragDrop/sensors';
import { useScrollCheck } from './useScrollCheck';

const ThankYouTextArea = styled(Textarea)(
  (props: { isRtlLanguage: boolean }) => `
direction: ${props.isRtlLanguage ? 'rtl' : 'ltr'};
width: 100%;
`,
);

const PreviewAndQuestionBankContainer = styled(Stack)<{ topNavHeight: number }>`
  background: ${tokens.color.neutral_100};
  width: 428px;
  flex-shrink: 0;
  align-items: start;
  margin-right: -${tokens.spacer.x6};

  height: calc(100vh - ${({ topNavHeight }) => topNavHeight}px);
  box-sizing: content-box;

  position: sticky;
  top: ${({ topNavHeight }) => topNavHeight}px;
`;

const QuestionListColumn = styled(Stack)`
  margin-left: -${tokens.spacer.x6};
  flex-grow: 1;
  min-width: 580px;
`;

export interface QuestionsSectionProps {
  connectVisitData: ConnectVisitData;
  onConnectVisitDataChange: (newValue: ConnectVisitData) => void;
  reloadPreview: (target: string) => void;
  previewConfig: WidgetConfig;
  questions: SurveyQuestion[];
  thankYouMessage: string;
  language: string;
  onThankYouMessageChange: (thankYouMessage: string) => void;
  onAddNewQuestion: (question: QuestionTypes | 'yes-no', index: number) => void;
  onAddQuestionFromQuestionBank: (question: QuestionBankQuestion, index: number) => void;
  onRemoveQuestion: (question: SurveyQuestion) => void;
  onUpdateQuestion: (fieldToUpdate: string[], questionId: string, isQuestionValid: boolean) => void;
  onShowLegalChange: () => void;
  onChangeOrder: (questions: SurveyQuestion[]) => void;
  onPreviewReaction: (config: { questionId: number; style: ReactionStyle }) => void;
  isValidateEnabled: boolean;
  isThankYouValid: boolean;
  isPopoverSurvey: boolean;
  isDataFromTemplate: boolean;
  hasLegalDetails: boolean;
  hasSurveyResponses: boolean;
  showLegal: boolean;
  areQuestionsValid: boolean;
}

function reorder(
  list: SurveyQuestion[],
  sourceIndex: number,
  destinationIndex: number,
): SurveyQuestion[] {
  const result = Array.from(list);
  const [removed] = result.splice(sourceIndex, 1);
  result.splice(destinationIndex, 0, removed);

  return result;
}

export const QuestionsSection = ({
  connectVisitData,
  onConnectVisitDataChange,
  reloadPreview,
  previewConfig,
  questions,
  thankYouMessage,
  language,
  onThankYouMessageChange,
  onAddQuestionFromQuestionBank,
  onAddNewQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
  onShowLegalChange,
  onChangeOrder,
  onPreviewReaction,
  isValidateEnabled,
  isThankYouValid,
  isPopoverSurvey,
  isDataFromTemplate,
  hasLegalDetails,
  hasSurveyResponses,
  showLegal,
  areQuestionsValid,
}: QuestionsSectionProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [isQuestionBankDragging, setIsQuestionBankDragging] = useState<boolean>(false);

  const onBeforeDragStart = useCallback(({ source }: DragStart) => {
    if (source.droppableId === 'question-bank') {
      setIsQuestionBankDragging(true);
    }
  }, []);

  const onDragEnd = useCallback(
    ({ source, destination, reason }: DropResult) => {
      setIsQuestionBankDragging(false);

      if (!destination || reason === 'CANCEL') {
        return;
      }

      if (source.droppableId === 'question-bank') {
        const question = ALL_QUESTIONS[source.index];
        if (question === undefined) return;

        const index =
          destination.droppableId === 'at-end'
            ? -1
            : questions.findIndex((q) => q.uuid === destination.droppableId);

        onAddQuestionFromQuestionBank(question, index);
        return;
      }

      onChangeOrder(reorder(questions, source.index, destination.index));
    },
    [onAddQuestionFromQuestionBank, onChangeOrder, questions],
  );

  const { topBarHeight: topNavHeight } = useNavigation();
  const { t } = useTranslation('feedback');
  const isRtlLanguage = useMemo(() => {
    if (language === undefined) return false;
    return rtlLanguages.indexOf(language) > -1;
  }, [language]);

  const { isScrolling, reloadPreviewRef } = useScrollCheck();

  const reloadPreviewCallback = useCallback(
    (step: string) => {
      if (isScrolling.current === false) reloadPreview(step);
      else reloadPreviewRef.current = () => reloadPreview(step);
    },
    [reloadPreview, isScrolling, reloadPreviewRef],
  );

  return (
    <DragDropContext
      onBeforeDragStart={onBeforeDragStart}
      onDragEnd={onDragEnd}
      sensors={customSensors}
      enableDefaultSensors={false}
    >
      <Stack direction="row">
        <QuestionListColumn direction="column" spacing="x4">
          {isDataFromTemplate && (
            <Alert kind="warning">{t('wizard.section.questions.template-reminder-alert')}</Alert>
          )}

          {isValidateEnabled && !areQuestionsValid && (
            <Alert kind="danger">{t('wizard.section.questions.review-alert')}</Alert>
          )}

          <QuestionList
            isQuestionBankDragging={isQuestionBankDragging}
            isValidateEnabled={isValidateEnabled}
            isRtlLanguage={isRtlLanguage}
            hasSurveyResponses={hasSurveyResponses}
            questions={questions}
            onAddNewQuestion={onAddNewQuestion}
            onRemoveQuestion={onRemoveQuestion}
            onUpdateQuestion={onUpdateQuestion}
            onPreviewReaction={onPreviewReaction}
            reloadPreview={reloadPreviewCallback}
          />

          <QuestionContainer
            index={questions.length}
            onMouseEnter={() => reloadPreview('thankYou')}
            isDraggable={false}
            title={t('wizard.section.questions.thank-you-message-title')}
          >
            <Stack direction="column" spacing="x0pt5">
              <ThankYouTextArea
                rows={2}
                id="thankyou"
                name="thankyou"
                isRequired
                placeholder={t('wizard.section.questions.thank-you-message-label')}
                aria-label={t('wizard.section.questions.thank-you-message-label')}
                defaultValue={thankYouMessage}
                onChange={(event) => {
                  onThankYouMessageChange(event.target.value);
                  reloadPreview('thankYou');
                }}
                onFocus={() => reloadPreview('thankYou')}
                isInvalid={!isThankYouValid}
                isRtlLanguage={isRtlLanguage}
              />
              {!isThankYouValid && (
                <Text textColor="text_error">
                  {t('wizard.section.questions.empty-field-error')}
                </Text>
              )}
            </Stack>

            <LegalInformation
              hasLegalDetails={hasLegalDetails}
              showLegal={showLegal}
              onShowLegalChange={onShowLegalChange}
            />

            <QuestionConnectVisitData
              connectVisitData={connectVisitData}
              onChange={onConnectVisitDataChange}
            />
          </QuestionContainer>
        </QuestionListColumn>
        <PreviewAndQuestionBankContainer direction="column" topNavHeight={topNavHeight}>
          <PreviewAndQuestionBankPanel
            isPopoverSurvey={isPopoverSurvey}
            previewConfig={previewConfig}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
          />
        </PreviewAndQuestionBankContainer>
      </Stack>
    </DragDropContext>
  );
};
